import { FC, useState } from "react";
import { useSignUpController } from "./controller";

import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Input,
  Select
} from "shared/components";
import { Tab } from "@headlessui/react";
import { FirstFormKeys, PMS } from "./constants";
import { Link } from "react-router-dom";
import { cx } from "utils/classnames";
import { toast } from "react-toastify";

import logoSquare from "shared/branding/logo-square.svg";

export const SignUpView: FC = () => {
  const { register, onSubmit, handleSubmit, isSubmitting } =
    useSignUpController();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [termsSelected, setTermsSelected] = useState(false);

  return (
    <div className="animate-appear flex min-h-screen items-center justify-center bg-background-900 p-2">
      <div className="h-full w-full max-w-[460px] rounded-lg border border-alpha-100 bg-background-1000 p-8 shadow-xl">
        <div className="flex items-center gap-4">
          <img alt="Boom logo" className="h-10 w-10" src={logoSquare} />
          <div>
            <p className="text-base font-bold">Boom</p>
            <p className="text-sm font-medium text-content-200">
              for Property Managers
            </p>
          </div>
        </div>
        <div className="my-6 border-b border-alpha-40" />
        <form
          className="mb-4"
          onSubmit={onSubmit({
            onSuccess: () => handleSubmit(),
            onError: (e) => {
              if (
                Object.values(FirstFormKeys).some((key) =>
                  Object.prototype.hasOwnProperty.call(e, key)
                )
              )
                setSelectedIndex(0);

              toast.error(Object.values(e).join(", "));
            }
          })}
        >
          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            <Tab.List className="mb-6 flex justify-between">
              <p className="text-xl font-bold text-content-1000">
                Create account
              </p>
              <div className="flex items-center justify-center">
                <Tab as="div">
                  {() => (
                    <div className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-1000 focus:outline-none">
                      1
                    </div>
                  )}
                </Tab>
                <div className="w-6 border-t border-alpha-100" />
                <Tab as="div">
                  {({ selected }) => (
                    <div
                      className={cx(
                        "flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full",
                        selected
                          ? "bg-primary-500 text-background-1000"
                          : "bg-alpha-40 text-alpha-300"
                      )}
                    >
                      2
                    </div>
                  )}
                </Tab>
              </div>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel className="flex flex-col gap-2">
                <Input
                  id="first_name"
                  label="First name"
                  placeholder="First name"
                  required={true}
                  {...register("first_name")}
                />
                <Input
                  id="last_name"
                  label="Last name"
                  placeholder="Last name"
                  required={true}
                  {...register("last_name")}
                />
                <Input
                  id="email"
                  label="Email address"
                  placeholder="example@gmail.com"
                  required={true}
                  {...register("email")}
                />
                <Input
                  id="password"
                  label="Create password"
                  required={true}
                  type="password"
                  placeholder="·············"
                  {...register("password")}
                />
                <Input
                  id="phone"
                  label="Phone number"
                  required={true}
                  placeholder="(123) 000-0000"
                  type="tel"
                  {...register("phone", undefined, "(___) ___-____")}
                />
                <Button
                  variant={ButtonVariant.PRIMARY}
                  className="mt-6 w-full"
                  size={ButtonSize.BASE}
                  onClick={() => setSelectedIndex(1)}
                >
                  Continue
                </Button>
              </Tab.Panel>
              <Tab.Panel className="mb-6 flex flex-col gap-2">
                <Input
                  id="company_name"
                  label="Company name"
                  placeholder="Example Inc."
                  required={true}
                  {...register("company_name")}
                />
                <Input
                  id="company_website"
                  label="Company website"
                  placeholder="example@gmail.com"
                  {...register("website")}
                />
                <Input
                  id="units"
                  label="Units"
                  placeholder="100"
                  required={true}
                  {...register("units")}
                />
                <Select
                  label="Property management system"
                  id="property_management_system"
                  searchable={false}
                  options={PMS}
                  placeholder="Choose PMS"
                  hideOptional
                  {...register("property_management_system")}
                />
                <div className="mb-6 flex">
                  <Checkbox
                    checked={termsSelected}
                    id="termsOfUse"
                    label={
                      <p className="text-sm font-medium text-content-700">
                        I agree to Boom&apos;s{" "}
                        <a
                          className="cursor-pointer text-sm font-bold text-primary-500"
                          target="_blank"
                          rel="noreferrer"
                          href={encodeURI(
                            "legal/Boom Terms of Use - Primary (December 2024).pdf"
                          )}
                        >
                          Terms of Use
                        </a>
                      </p>
                    }
                    name="termsSelected"
                    onChange={() => setTermsSelected(!termsSelected)}
                  />
                </div>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  className="w-full"
                  size={ButtonSize.BASE}
                  type="submit"
                  disabled={!termsSelected}
                  loading={isSubmitting}
                >
                  Sign Up
                </Button>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </form>
        <p className="text-center text-sm font-medium text-content-400">
          Already have an account{" "}
          <Link
            className="cursor-pointer text-sm font-bold text-primary-500"
            to="/login"
          >
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};
